/**
 * @fileoverview Syndicator Showdown — display-state helpers for the
 * Upcoming Schedule feature (upcoming_meetings/{meetingKey}).
 * @module utils/upcomingMeetingStatus
 *
 * Pure, dependency-free helpers shared by:
 *   - DashboardPage.jsx ("Upcoming Schedule" section)
 *   - ShowdownsPage.jsx (PLANNED cards in the Upcoming tab)
 *
 * A scheduled meeting only ever reaches the user in one of two shapes:
 *   1. status === 'scheduled'        — admin-planned, no real Showdown yet.
 *      Always maps to the "Planned" label. Never clickable/routable.
 *   2. status === 'showdown_created' — a real game_slates doc now exists.
 *      Once converted, the meeting is just a thin pointer (`convertedSlateId`)
 *      — the actual lifecycle (Opening Soon / Entries Open / Live / Completed)
 *      is owned entirely by utils/showdownStatus.js's getShowdownDisplayState,
 *      applied to the linked slate. This file does NOT re-derive that lifecycle;
 *      callers pass the already-computed slate `state` in as `linkedSlateState`.
 *
 * candidate/skipped/archived meetings are never queried by user-facing code
 * (Firestore rules also block reading them as a signed-out/non-admin user),
 * so this file has no branch for them.
 */

export const PLANNED_TONE = 'planned';

/**
 * Formats an ISO date ("2026-07-06") as a short display date ("Mon 06 Jul").
 * Returns the raw input if it cannot be parsed.
 */
export function formatMeetingDateShort(meetingDateIso) {
  if (!meetingDateIso) return '';
  const d = new Date(`${meetingDateIso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return meetingDateIso;
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short' });
}

/**
 * Builds the suggested Showdown title used both to prefill AdminSetupPage
 * and to label a not-yet-converted planned meeting: "{Track} Showdown".
 */
export function suggestedShowdownTitle(meeting) {
  const track = String(meeting?.trackName || '').trim();
  return track ? `${track} Showdown` : 'Showdown';
}

/**
 * Returns the unified display state for an upcoming_meetings doc.
 *
 * @param {object} meeting            upcoming_meetings/{meetingKey} doc data (+ id)
 * @param {object|null} linkedSlateState  the result of getShowdownDisplayState(slate)
 *                                        for meeting.convertedSlateId, if the
 *                                        caller has already loaded/computed it.
 *                                        Ignored unless meeting.status === 'showdown_created'.
 *
 * @returns {{
 *   label: string,
 *   tone: string,
 *   isPlanned: boolean,
 *   isClickable: boolean,
 *   route: string|null,
 * }}
 */
export function getUpcomingMeetingDisplayState(meeting, linkedSlateState = null) {
  if (!meeting) {
    return { label: 'Planned', tone: PLANNED_TONE, isPlanned: true, isClickable: false, route: null };
  }

  if (meeting.status === 'showdown_created') {
    if (linkedSlateState) {
      // Map the slate's own lifecycle onto the 5 user-facing Upcoming
      // Schedule labels the feature brief specifies.
      const label = linkedSlateState.lifecycle === 'openingSoon' ? 'Opening Soon'
        : linkedSlateState.lifecycle === 'entriesOpen' ? 'Entries Open'
        : linkedSlateState.lifecycle === 'live' ? 'Live'
        : linkedSlateState.lifecycle === 'resultsPending' ? 'Live'
        : linkedSlateState.lifecycle === 'completed' ? 'Completed'
        : 'Opening Soon';

      return {
        label,
        tone: linkedSlateState.statusTone || 'soon',
        isPlanned: false,
        isClickable: !linkedSlateState.primaryActionDisabled && !!linkedSlateState.primaryActionRoute,
        route: linkedSlateState.primaryActionRoute || (meeting.convertedSlateId ? `/slate/${meeting.convertedSlateId}` : null),
      };
    }
    // Converted but the linked slate hasn't loaded yet — still not "planned"
    // (a real Showdown exists), but we don't know its lifecycle yet.
    return {
      label: 'Opening Soon',
      tone: 'soon',
      isPlanned: false,
      isClickable: false,
      route: meeting.convertedSlateId ? `/slate/${meeting.convertedSlateId}` : null,
    };
  }

  // status === 'scheduled' (the only other status a user can ever read).
  return { label: 'Planned', tone: PLANNED_TONE, isPlanned: true, isClickable: false, route: null };
}
